// dependencies
import { UseQueryResult, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

// api
import {
	Goal,
	GoalChecklistItem,
	GoalHistory,
	IGoalResponse,
	PatchGoalData,
	PostGoalData,
	PostGoalHistoryData,
	createGoalsInternalApi,
	deleteGoal,
	deleteGoalChecklistItems,
	deleteGoalHistoryEntry,
	fetchGoalDetailHistory,
	fetchGoalDetails,
	patchGoal,
	patchGoalChecklistItems,
	patchGoalHistoryEntry,
	postGoal,
	postGoalChecklistItems,
	postGoalHistoryEntry,
} from "../../services";
import StandardQueryArguments, { PYUseMutationOptions } from "../StandardQueryArguments";

export type GoalDetailsQueryArguments = StandardQueryArguments<Goal> & {
	goalID?: string;
	disableDefaultErrorHandling?: boolean;
};

const calculateGoalDetailsQuery = (goalID: string = "") => ({
	queryKey: ["goal-details", goalID],
	retry: 1,
	queryFn: async () => await fetchGoalDetails(goalID),
	enabled: !!goalID,
});

export const useGoalDetails = ({
	goalID = "",
	disableDefaultErrorHandling = false,
}: GoalDetailsQueryArguments): UseQueryResult<IGoalResponse> => {
	return useQuery({
		meta: {
			errorMessage: "Error getting goal details",
			disableDefaultErrorHandling,
		},
		...calculateGoalDetailsQuery(goalID),
	});
};

export type GoalHistoryQueryArguments = StandardQueryArguments<GoalHistory[]> & {
	type?: string;
	goalID: string;
	disableDefaultErrorHandling?: boolean;
};

export const useGoalHistory = ({
	goalID,
	skip = 0,
	take = 100,
	type,
	sortDirection,
	sortName,
	disableDefaultErrorHandling,
}: GoalHistoryQueryArguments) => {
	return useQuery({
		queryKey: ["goal-history", goalID, skip, take, type, sortDirection, sortName],
		retry: 1,
		queryFn: async () => {
			const results = await fetchGoalDetailHistory(goalID, skip, take, type, sortDirection, sortName);

			return results;
		},
		meta: {
			errorMessage: "Error getting goal history",
			disableDefaultErrorHandling,
		},
	});
};

export type GoalHistoryPostMutateArguments = {
	goalID: string;
	data: PostGoalHistoryData;
};

export const useMutatePostGoalHistory = (options: PYUseMutationOptions) => {
	return useMutation({
		mutationKey: ["goal-history-mutation"],
		mutationFn: async ({ data, goalID }: GoalHistoryPostMutateArguments) => postGoalHistoryEntry(goalID, data),
		...options,
	});
};

export const useMutatePatchGoalHistory = (options: PYUseMutationOptions) => {
	return useMutation({
		mutationKey: ["goal-history-mutation"],
		mutationFn: async ({ data, goalID }: GoalHistoryPostMutateArguments) => patchGoalHistoryEntry(goalID, data),
		...options,
	});
};

export type GoalHistoryDeleteMutateArguments = {
	goalHistoryID: string;
	goalID: string;
};
export const useMutateDeleteGoalHistory = (options: PYUseMutationOptions<GoalHistoryDeleteMutateArguments>) => {
	return useMutation({
		mutationKey: ["goal-history-mutation"],
		mutationFn: async ({ goalHistoryID, goalID }: GoalHistoryDeleteMutateArguments) =>
			deleteGoalHistoryEntry(goalHistoryID, goalID),
		...options,
	});
};

export const useMutateGoalDetails = (goalID: string, options: PYUseMutationOptions) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationKey: ["goal-details-mutation", goalID],
		mutationFn: async (data: PatchGoalData) => {
			const response = await patchGoal(goalID, data);
			//refetch the goal details query cache
			queryClient.refetchQueries({ queryKey: ["goal-details", goalID] });
			return response;
		},
		...options,
	});
};

export const useMutateAddGoalChecklistItems = (goalID: string, onSuccess: (_response) => void, onError = () => {}) => {
	return useMutation({
		mutationKey: ["goal-checklist-create-mutation", goalID],
		mutationFn: async (checklist: GoalChecklistItem[]) => await postGoalChecklistItems(goalID, checklist),
		onSuccess,
		onError,
	});
};

export const useMutateDeleteGoalChecklistItems = (goalID: string, onSuccess: () => void, onError = () => {}) => {
	return useMutation({
		mutationKey: ["goal-checklist-delete-mutation", goalID],
		mutationFn: async (checklistIDs: string[]) => await deleteGoalChecklistItems(goalID, checklistIDs),
		onSuccess,
		onError,
	});
};

export const useMutateUpdateGoalChecklistItems = (goalID: string, onSuccess: () => void, onError = () => {}) => {
	return useMutation({
		mutationKey: ["goal-checklist-update-mutation", goalID],
		mutationFn: async (checklist: GoalChecklistItem[]) => await patchGoalChecklistItems(goalID, checklist),
		onSuccess,
		onError,
	});
};

export const useMutateCreateGoal = (options: PYUseMutationOptions, useInternalApi: boolean = true) => {
	return useMutation({
		mutationKey: ["goal-details-creation"],
		mutationFn: async (data: PostGoalData) => (useInternalApi ? createGoalsInternalApi(data) : postGoal(data)),
		...options,
	});
};

export const useDeleteGoal = (options: PYUseMutationOptions) => {
	return useMutation({
		mutationKey: ["goal-details-deletion"],
		mutationFn: async (goalID: string) => deleteGoal(goalID),
		...options,
	});
};
